import FeatureReference from './FeatureReference.js';
/**
 * @memberof feature-references
 * @classdesc
 * {@linkcode FeatureRecord}s represent the populated result of an API interaction
 * requesting features. Each instance reliably includes a label & geometry.
 * Data is optionally populated.
 *
 * NOTE: This was specifically named to prevent conflicting "names" with a
 * GeoJSON Feature. While similar, we don't expect them to be equivalent.
 *
 * @property {string} label
 * @property {feature-references.FeatureReference} ref
 * @property {GeoJSON.Geometry} geometry
 * @property {object} data
 */
class FeatureRecord {
    /**
     * @param {object} props
     * @param {string} props.label
     * @param {feature-references.FeatureReference} props.ref
     * @param {GeoJSON.Geometry} props.geometry
     * @param {object} [props.data={}]
     */
    constructor(props) {
        this.label = props.label;
        this.ref = new FeatureReference(props.ref);
        this.geometry = props.geometry;
        this.data = props.data || {};
    }
    /**
     * @type {string}
     */
    get key() {
        return this.ref.key;
    }
    get id() {
        return this.key;
    }
    /**
     * @return {GeoJSON.Feature}
     */
    toGeoJSON() {
        return {
            id: this.ref.key,
            type: 'Feature',
            properties: null,
            geometry: this.geometry || {
                type: 'MultiPolygon',
                coordinates: [[]],
            },
        };
    }
    /**
     * @param {GeoJSON.Feature} feature
     * @return {feature-references.FeatureRecord}
     */
    static fromGeoJSON(feature) {
        const ref = FeatureReference.fromKey(feature.id.toString());
        const label = feature.label;
        const record = new FeatureRecord({
            ref,
            label,
            geometry: feature.geometry,
            data: feature.properties,
        });
        return record;
    }
    /**
     * @param {GeoJSON.FeatureCollection} featureCollection
     * @return {feature-references.FeatureRecord[]}
     */
    static fromGeoJSONFeatureCollection(featureCollection) {
        const features = featureCollection.features || [];
        return features.map(FeatureRecord.fromGeoJSON);
    }
}
export default FeatureRecord;
